import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const HeroSection = () => {
  const [currentMetric, setCurrentMetric] = useState(0);

  const metrics = [
  { value: "100,000+", label: "Policies Tracked Daily", icon: "FileSearch" },
  { value: "Real-Time", label: "Policy Change Alerts", icon: "Bell" },
  { value: "All Payers", label: "Medicare, Medicaid, Commercial", icon: "Shield" }];


  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMetric((prev) => (prev + 1) % metrics?.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative bg-gradient-to-br from-[#1B1B20] via-[#1B1B20] to-[#0A0A0A] text-white overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-64 h-64 md:w-96 md:h-96 lg:w-[32rem] lg:h-[32rem] bg-[#1A73E8] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-64 h-64 md:w-96 md:h-96 lg:w-[32rem] lg:h-[32rem] bg-[#ED1C24] rounded-full blur-3xl"></div>
      </div>
      <div className="relative max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-16 md:py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 lg:gap-16 items-center">
          <div className="space-y-6 md:space-y-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-[#1A73E8]/20 rounded-full border border-[#1A73E8]/30">
              <Icon name="TrendingUp" size={16} color="#1A73E8" />
              <span className="text-xs md:text-sm font-medium text-[#1A73E8]">Monitoring 100,000+ Policies Daily</span>
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold leading-tight">
              Medical Policy Changes,<br />
              <span className="text-[#ED1C24]">Monitored in Real-Time.</span>
            </h1>

            <p className="text-base md:text-lg lg:text-xl text-gray-300 leading-relaxed">
              Never miss critical coverage policy changes. PolicyAlerts monitors medical policies across commercial, Medicare, and Medicaid insurers—delivering instant alerts when changes impact your practice or products.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
              <Button
                variant="default"
                size="lg"
                iconName="Calendar"
                iconPosition="left"
                className="bg-[#ED1C24] hover:bg-[#ED1C24]/90 text-white"
                asChild>

                <Link to="/request-demo">Request Demo</Link>
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Play"
                iconPosition="left"
                className="border-white/30 text-white hover:bg-white/10"
                asChild>

                <Link to="/platform-demo">See How It Works</Link>
              </Button>
            </div>

            <div className="flex items-center gap-6 md:gap-8 pt-4 md:pt-6">
              <div className="flex -space-x-3">
                {[
                { src: "https://img.rocket.new/generatedImages/rocket_gen_img_1cbee50a4-1763296279384.png", alt: "Professional headshot of Caucasian male executive with gray hair in navy suit and red tie" },
                { src: "https://img.rocket.new/generatedImages/rocket_gen_img_15f0f9864-1763300711001.png", alt: "Professional headshot of Asian female executive with black hair in charcoal blazer" },
                { src: "https://img.rocket.new/generatedImages/rocket_gen_img_12c70a66b-1763293810497.png", alt: "Professional headshot of African American male executive with short hair in dark blue suit" },
                { src: "https://img.rocket.new/generatedImages/rocket_gen_img_19dd7eb8c-1763293980389.png", alt: "Professional headshot of Hispanic female executive with brown hair in black business attire" }]?.
                map((user, idx) =>
                <Image
                  key={idx}
                  src={user?.src}
                  alt={user?.alt}
                  className="w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-[#1B1B20] object-cover" />

                )}
              </div>
              <div>
                <p className="text-sm md:text-base font-semibold">Trusted by Healthcare Leaders</p>
                <p className="text-xs md:text-sm text-gray-400">Provider groups, specialty practices, and manufacturers</p>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="bg-gradient-to-br from-[#1A73E8]/20 to-[#ED1C24]/20 rounded-2xl p-4 md:p-6 lg:p-8 backdrop-blur-sm border border-white/10">
              <div className="bg-[#1B1B20]/80 rounded-xl p-4 md:p-6 space-y-4 md:space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-base md:text-lg font-semibold">Live Policy Monitor</h3>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs md:text-sm text-gray-400">Live</span>
                  </div>
                </div>

                <div className="space-y-3 md:space-y-4">
                  {metrics?.map((metric, idx) => (
                    <div
                      key={idx}
                      className={`p-3 md:p-4 rounded-lg transition-all duration-500 ${
                        currentMetric === idx
                          ? 'bg-[#1A73E8]/20 border border-[#1A73E8]/30 scale-105'
                          : 'bg-white/5 border border-white/10'
                      }`}>
                      <div className="flex items-center gap-3">
                        <Icon name={metric?.icon} size={20} color={currentMetric === idx ? '#1A73E8' : '#9CA3AF'} />
                        <div>
                          <p className="text-xl md:text-2xl font-bold">{metric?.value}</p>
                          <p className="text-xs md:text-sm text-gray-400">{metric?.label}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="pt-3 md:pt-4 border-t border-white/10">
                  <p className="text-xs md:text-sm text-gray-400 text-center">
                    Updated every 24 hours • Comprehensive coverage tracking
                  </p>
                </div>
              </div>
            </div>

            <div className="absolute -bottom-4 -right-4 w-24 h-24 md:w-32 md:h-32 bg-[#ED1C24]/20 rounded-full blur-2xl"></div>
            <div className="absolute -top-4 -left-4 w-24 h-24 md:w-32 md:h-32 bg-[#1A73E8]/20 rounded-full blur-2xl"></div>
          </div>
        </div>
      </div>
    </section>);

};

export default HeroSection;